/**
 * API Error Codes
 *
 * Centralized error code definitions for all API integrations.
 * Used for consistent error identification across the stack.
 */

/**
 * All possible API error codes
 */
export const API_ERROR_CODES = {
  // ============================================
  // Google Maps/Places Errors
  // ============================================
  GOOGLE_AUTH_FAILED: "GOOGLE_AUTH_FAILED",
  GOOGLE_QUOTA_EXHAUSTED: "GOOGLE_QUOTA_EXHAUSTED",
  GOOGLE_RATE_LIMITED: "GOOGLE_RATE_LIMITED",
  GOOGLE_INVALID_REQUEST: "GOOGLE_INVALID_REQUEST",
  GOOGLE_REQUEST_DENIED: "GOOGLE_REQUEST_DENIED",
  GOOGLE_ZERO_RESULTS: "GOOGLE_ZERO_RESULTS",
  GOOGLE_SERVER_ERROR: "GOOGLE_SERVER_ERROR",

  // ============================================
  // FindyMail Errors
  // ============================================
  FINDYMAIL_AUTH_FAILED: "FINDYMAIL_AUTH_FAILED",
  FINDYMAIL_CREDITS_EXHAUSTED: "FINDYMAIL_CREDITS_EXHAUSTED",
  FINDYMAIL_SUBSCRIPTION_PAUSED: "FINDYMAIL_SUBSCRIPTION_PAUSED",
  FINDYMAIL_RATE_LIMITED: "FINDYMAIL_RATE_LIMITED",
  FINDYMAIL_SERVER_ERROR: "FINDYMAIL_SERVER_ERROR",
  FINDYMAIL_INVALID_REQUEST: "FINDYMAIL_INVALID_REQUEST",
  FINDYMAIL_NO_CONTACTS_FOUND: "FINDYMAIL_NO_CONTACTS_FOUND",

  // ============================================
  // Perplexity Errors
  // ============================================
  PERPLEXITY_AUTH_FAILED: "PERPLEXITY_AUTH_FAILED",
  PERPLEXITY_CREDITS_EXHAUSTED: "PERPLEXITY_CREDITS_EXHAUSTED",
  PERPLEXITY_RATE_LIMITED: "PERPLEXITY_RATE_LIMITED",
  PERPLEXITY_RATE_EXCEEDED: "PERPLEXITY_RATE_EXCEEDED",
  PERPLEXITY_SERVER_ERROR: "PERPLEXITY_SERVER_ERROR",
  PERPLEXITY_TIMEOUT: "PERPLEXITY_TIMEOUT",

  // ============================================
  // Tavily Errors
  // ============================================
  TAVILY_AUTH_FAILED: "TAVILY_AUTH_FAILED",
  TAVILY_CREDITS_EXHAUSTED: "TAVILY_CREDITS_EXHAUSTED",
  TAVILY_RATE_LIMITED: "TAVILY_RATE_LIMITED",
  TAVILY_SERVER_ERROR: "TAVILY_SERVER_ERROR",

  // ============================================
  // OpenAI Errors
  // ============================================
  OPENAI_AUTH_FAILED: "OPENAI_AUTH_FAILED",
  OPENAI_QUOTA_EXHAUSTED: "OPENAI_QUOTA_EXHAUSTED",
  OPENAI_RATE_LIMITED: "OPENAI_RATE_LIMITED",
  OPENAI_SERVER_ERROR: "OPENAI_SERVER_ERROR",
  OPENAI_CONTEXT_LENGTH: "OPENAI_CONTEXT_LENGTH",

  // ============================================
  // FastSpring (Billing) Errors
  // ============================================
  FASTSPRING_AUTH_FAILED: "FASTSPRING_AUTH_FAILED",
  FASTSPRING_PAYMENT_FAILED: "FASTSPRING_PAYMENT_FAILED",
  FASTSPRING_ORDER_NOT_FOUND: "FASTSPRING_ORDER_NOT_FOUND",
  FASTSPRING_SERVER_ERROR: "FASTSPRING_SERVER_ERROR",

  // ============================================
  // Instantly Errors
  // ============================================
  INSTANTLY_AUTH_FAILED: "INSTANTLY_AUTH_FAILED",
  INSTANTLY_RATE_LIMITED: "INSTANTLY_RATE_LIMITED",
  INSTANTLY_SERVER_ERROR: "INSTANTLY_SERVER_ERROR",

  // ============================================
  // Generic/Common Errors
  // ============================================
  NETWORK_ERROR: "NETWORK_ERROR",
  TIMEOUT_ERROR: "TIMEOUT_ERROR",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
} as const;

/**
 * Type for any valid API error code
 */
export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];

/**
 * Group error codes by provider for easier lookup
 */
export const ERROR_CODES_BY_PROVIDER = {
  google_maps: [
    API_ERROR_CODES.GOOGLE_AUTH_FAILED,
    API_ERROR_CODES.GOOGLE_QUOTA_EXHAUSTED,
    API_ERROR_CODES.GOOGLE_RATE_LIMITED,
    API_ERROR_CODES.GOOGLE_INVALID_REQUEST,
    API_ERROR_CODES.GOOGLE_REQUEST_DENIED,
    API_ERROR_CODES.GOOGLE_ZERO_RESULTS,
    API_ERROR_CODES.GOOGLE_SERVER_ERROR,
  ],
  google_places: [
    API_ERROR_CODES.GOOGLE_AUTH_FAILED,
    API_ERROR_CODES.GOOGLE_QUOTA_EXHAUSTED,
    API_ERROR_CODES.GOOGLE_RATE_LIMITED,
    API_ERROR_CODES.GOOGLE_INVALID_REQUEST,
    API_ERROR_CODES.GOOGLE_REQUEST_DENIED,
    API_ERROR_CODES.GOOGLE_ZERO_RESULTS,
    API_ERROR_CODES.GOOGLE_SERVER_ERROR,
  ],
  findymail: [
    API_ERROR_CODES.FINDYMAIL_AUTH_FAILED,
    API_ERROR_CODES.FINDYMAIL_CREDITS_EXHAUSTED,
    API_ERROR_CODES.FINDYMAIL_SUBSCRIPTION_PAUSED,
    API_ERROR_CODES.FINDYMAIL_RATE_LIMITED,
    API_ERROR_CODES.FINDYMAIL_SERVER_ERROR,
    API_ERROR_CODES.FINDYMAIL_INVALID_REQUEST,
    API_ERROR_CODES.FINDYMAIL_NO_CONTACTS_FOUND,
  ],
  perplexity: [
    API_ERROR_CODES.PERPLEXITY_AUTH_FAILED,
    API_ERROR_CODES.PERPLEXITY_CREDITS_EXHAUSTED,
    API_ERROR_CODES.PERPLEXITY_RATE_LIMITED,
    API_ERROR_CODES.PERPLEXITY_RATE_EXCEEDED,
    API_ERROR_CODES.PERPLEXITY_SERVER_ERROR,
    API_ERROR_CODES.PERPLEXITY_TIMEOUT,
  ],
  tavily: [
    API_ERROR_CODES.TAVILY_AUTH_FAILED,
    API_ERROR_CODES.TAVILY_CREDITS_EXHAUSTED,
    API_ERROR_CODES.TAVILY_RATE_LIMITED,
    API_ERROR_CODES.TAVILY_SERVER_ERROR,
  ],
  openai: [
    API_ERROR_CODES.OPENAI_AUTH_FAILED,
    API_ERROR_CODES.OPENAI_QUOTA_EXHAUSTED,
    API_ERROR_CODES.OPENAI_RATE_LIMITED,
    API_ERROR_CODES.OPENAI_SERVER_ERROR,
    API_ERROR_CODES.OPENAI_CONTEXT_LENGTH,
  ],
  fastspring: [
    API_ERROR_CODES.FASTSPRING_AUTH_FAILED,
    API_ERROR_CODES.FASTSPRING_PAYMENT_FAILED,
    API_ERROR_CODES.FASTSPRING_ORDER_NOT_FOUND,
    API_ERROR_CODES.FASTSPRING_SERVER_ERROR,
  ],
  instantly: [
    API_ERROR_CODES.INSTANTLY_AUTH_FAILED,
    API_ERROR_CODES.INSTANTLY_RATE_LIMITED,
    API_ERROR_CODES.INSTANTLY_SERVER_ERROR,
  ],
  internal: [
    API_ERROR_CODES.NETWORK_ERROR,
    API_ERROR_CODES.TIMEOUT_ERROR,
    API_ERROR_CODES.VALIDATION_ERROR,
    API_ERROR_CODES.UNKNOWN_ERROR,
  ],
} as const;

/**
 * Check if an error code indicates a user-actionable error
 * (requires user to take action, not just wait/retry)
 */
export function isUserActionableError(errorCode: ApiErrorCode): boolean {
  const userActionableErrors: ApiErrorCode[] = [
    API_ERROR_CODES.GOOGLE_AUTH_FAILED,
    API_ERROR_CODES.GOOGLE_QUOTA_EXHAUSTED,
    API_ERROR_CODES.GOOGLE_REQUEST_DENIED,
    API_ERROR_CODES.FINDYMAIL_AUTH_FAILED,
    API_ERROR_CODES.FINDYMAIL_CREDITS_EXHAUSTED,
    API_ERROR_CODES.FINDYMAIL_SUBSCRIPTION_PAUSED,
    API_ERROR_CODES.PERPLEXITY_AUTH_FAILED,
    API_ERROR_CODES.PERPLEXITY_CREDITS_EXHAUSTED,
    API_ERROR_CODES.PERPLEXITY_RATE_EXCEEDED,
    API_ERROR_CODES.TAVILY_AUTH_FAILED,
    API_ERROR_CODES.TAVILY_CREDITS_EXHAUSTED,
    API_ERROR_CODES.OPENAI_AUTH_FAILED,
    API_ERROR_CODES.OPENAI_QUOTA_EXHAUSTED,
    API_ERROR_CODES.FASTSPRING_AUTH_FAILED,
    API_ERROR_CODES.FASTSPRING_PAYMENT_FAILED,
    API_ERROR_CODES.INSTANTLY_AUTH_FAILED,
  ];
  return userActionableErrors.includes(errorCode);
}

/**
 * Check if an error code indicates a retryable error
 */
export function isRetryableError(errorCode: ApiErrorCode): boolean {
  const retryableErrors: ApiErrorCode[] = [
    API_ERROR_CODES.GOOGLE_RATE_LIMITED,
    API_ERROR_CODES.FINDYMAIL_RATE_LIMITED,
    API_ERROR_CODES.FINDYMAIL_SERVER_ERROR,
    API_ERROR_CODES.PERPLEXITY_RATE_LIMITED,
    API_ERROR_CODES.PERPLEXITY_SERVER_ERROR,
    API_ERROR_CODES.PERPLEXITY_TIMEOUT,
    API_ERROR_CODES.TAVILY_RATE_LIMITED,
    API_ERROR_CODES.TAVILY_SERVER_ERROR,
    API_ERROR_CODES.OPENAI_RATE_LIMITED,
    API_ERROR_CODES.OPENAI_SERVER_ERROR,
    API_ERROR_CODES.FASTSPRING_SERVER_ERROR,
    API_ERROR_CODES.INSTANTLY_RATE_LIMITED,
    API_ERROR_CODES.INSTANTLY_SERVER_ERROR,
    API_ERROR_CODES.NETWORK_ERROR,
    API_ERROR_CODES.TIMEOUT_ERROR,
    API_ERROR_CODES.GOOGLE_SERVER_ERROR,
  ];
  return retryableErrors.includes(errorCode);
}
